import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { trackCart, track } from "@/lib/analytics";
import { useOffers, FALLBACK_FREE_SHIPPING_AT } from "@/lib/shop";

const CART_KEY = "karijeeva_cart_v1";
const WELCOME_KEY = "karijeeva_welcome_code_v1";
// Fallbacks only; live values come from /api/offers (Admin > Settings).
export const FREE_SHIPPING_AT = FALLBACK_FREE_SHIPPING_AT;
export const SHIPPING_FEE = 49;
const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(CART_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [coupon, setCoupon] = useState(null); // {code, type, final_discount_amount, message}
  const [welcomeCode, setWelcomeCode] = useState(null); // {code, expires_at}

  // Persist cart
  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  // Watch localStorage for welcome code (set by newsletter signup)
  const loadWelcome = useCallback(() => {
    try {
      const raw = localStorage.getItem(WELCOME_KEY);
      if (!raw) { setWelcomeCode(null); return; }
      const parsed = JSON.parse(raw);
      if (!parsed?.code || (parsed.expires_at && new Date(parsed.expires_at) < new Date())) {
        localStorage.removeItem(WELCOME_KEY);
        setWelcomeCode(null);
        return;
      }
      setWelcomeCode(parsed);
    } catch { setWelcomeCode(null); }
  }, []);

  useEffect(() => {
    loadWelcome();
    const onStorage = (e) => { if (e.key === WELCOME_KEY) loadWelcome(); };
    window.addEventListener("storage", onStorage);
    // Re-check every minute in case a dated code runs out
    const tick = setInterval(loadWelcome, 60000);
    return () => { window.removeEventListener("storage", onStorage); clearInterval(tick); };
  }, [loadWelcome]);

  // ----- Mutators -----
  const addItem = (item) => {
    setItems((cur) => {
      const idx = cur.findIndex((i) => i.variant_id === item.variant_id);
      if (idx >= 0) {
        const next = [...cur];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + (item.quantity || 1) };
        return next;
      }
      return [...cur, { ...item, quantity: item.quantity || 1 }];
    });
    trackCart("add_to_cart", [{ ...item, quantity: item.quantity || 1 }]);
    // The drawer opening is the confirmation; a toast on top of it only hides the basket.
    setDrawerOpen(true);
  };

  const setQty = (variant_id, quantity) => {
    if (quantity <= 0) return removeItem(variant_id);
    const line = items.find((i) => i.variant_id === variant_id);
    if (line && quantity !== line.quantity) {
      const delta = quantity - line.quantity;
      trackCart(delta > 0 ? "add_to_cart" : "remove_from_cart", [{ ...line, quantity: Math.abs(delta) }]);
    }
    setItems((cur) => cur.map((i) => (i.variant_id === variant_id ? { ...i, quantity } : i)));
  };

  const removeItem = (variant_id) => {
    const line = items.find((i) => i.variant_id === variant_id);
    if (line) trackCart("remove_from_cart", [line]);
    setItems((cur) => cur.filter((i) => i.variant_id !== variant_id));
  };

  const clear = () => { setItems([]); setCoupon(null); };

  // ----- Coupon -----
  const applyCoupon = async (code) => {
    try {
      const subtotalNow = items.reduce((s, i) => s + i.unit_price * i.quantity, 0);
      const { data } = await api.post("/coupons/validate", { code, subtotal: subtotalNow });
      if (data?.valid === false) {
        track("coupon_rejected", { coupon: String(code).toUpperCase() });
        toast.error(data.message || "Coupon not valid");
        return { ok: false, message: data.message };
      }
      setCoupon(data);
      track("coupon_applied", { coupon: data.code, discount: Number(data.final_discount_amount || 0) });
      toast.success(`Applied ${data.code}`, { description: data.message });
      return { ok: true };
    } catch (e) {
      // 404 still comes through as error, server returns body {valid:false, message}
      const body = e?.response?.data;
      if (body && body.valid === false) {
        track("coupon_rejected", { coupon: String(code).toUpperCase() });
        toast.error(body.message || "Coupon not valid");
        return { ok: false, message: body.message };
      }
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.msg || "Invalid coupon");
      toast.error(msg);
      return { ok: false };
    }
  };
  const removeCoupon = () => { setCoupon(null); };

  // ----- Derived -----
  const offers = useOffers();
  const freeAt = offers?.free_shipping_at ?? FREE_SHIPPING_AT;
  const fee = offers?.shipping_fee ?? SHIPPING_FEE;
  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.unit_price * i.quantity, 0);
    // Recompute from the code's rule on every change, the same way the server does.
    const rawDiscount = !coupon ? 0
      : coupon.type === "percent" ? Math.round(subtotal * (Number(coupon.discount_value) || 0)) / 100
      : coupon.type === "flat" ? Number(coupon.discount_value) || 0
      : coupon.final_discount_amount || 0;
    const discount = Math.min(rawDiscount, subtotal);
    const taxable = Math.max(0, subtotal - discount);
    // Prices are MRP, inclusive of GST. Mirrors _compute_totals on the server.
    const gst = Math.round((taxable - taxable / 1.05) * 100) / 100;
    // Judged before discounts, same as the server: a code never adds the fee back.
    const shipping = subtotal >= freeAt || subtotal === 0 ? 0 : fee;
    const total = Math.round((taxable + shipping) * 100) / 100;
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const toFreeShipping = subtotal === 0 ? freeAt : Math.max(0, freeAt - subtotal);
    return { subtotal, discount, gst, shipping, total, count, toFreeShipping, freeShippingAt: freeAt };
  }, [items, coupon, freeAt, fee]);

  return (
    <CartContext.Provider
      value={{
        items, totals, coupon, welcomeCode,
        drawerOpen, setDrawerOpen,
        addItem, setQty, removeItem, clear,
        applyCoupon, removeCoupon, reloadWelcome: loadWelcome,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}

// Utility for the newsletter hook to write welcome code
export function saveWelcomeCode(code, expiresAt) {
  try {
    localStorage.setItem(
      "karijeeva_welcome_code_v1",
      JSON.stringify({ code, expires_at: expiresAt })
    );
    window.dispatchEvent(new StorageEvent("storage", { key: "karijeeva_welcome_code_v1" }));
  } catch {}
}
